import imgURL from '../../lib/imgURL';
import { CleverTapEventNames, UiRoutes } from '../../lib/constants';
import { CleverTapEventConfigName } from 'lib/typing';
import { checkPermissions } from 'lib/helper';
import { isCitiTheme } from 'lib/analyticsUtil';
import { Icons } from 'components/SVGIcon/icons.constants';
import { PG_ROUTER_ADMIN_LINKS } from '../../screens/Products/Plural/PgRouterAdmin/pgRouterAdminConfig';

const isCiti = isCitiTheme();

export const SidebarLabels = {
  COMPLETE_PROFILE: "Complete KYC",
  SUBMIT_DETAILS: "Submit your details to start accepting payments",
};

// Maps stepper display label → backend step_name in lead_progress
export const STEP_LABEL_TO_BACKEND_STEP: Record<string, string> = {
  'Review charges': 'MDR_ACCEPTANCE',
  'Products': 'CHOOSE_PRODUCTS',
  'PAN & GST': 'PAN_GST_DETAILS',
  'Website details': 'BUSINESS_DETAILS',
  'Bank account': 'BANK_DETAILS',
  'KYC verification': 'KYC_VERIFICATION',
  'Video KYC': 'VIDEO_KYC_VERIFICATION',
  'Review all details': 'REVIEW_DETAILS',
  'Review details': 'REVIEW_DETAILS',
  'Upload documents': 'DOCUMENTS_UPLOAD',
  'KYC & Sign Agreement': 'KYC_AGREEMENT_SIGN',
  'Business verification': 'DOCUMENTS_UPLOAD',
};

// Reverse map: backend step_name → display label (used for chat step dividers, etc.)
// When multiple labels map to the same backend name, the canonical/longer form wins.
export const BACKEND_STEP_TO_DISPLAY_LABEL: Record<string, string> = Object.entries(
  STEP_LABEL_TO_BACKEND_STEP
).reduce((acc, [label, backend]) => {
  // Prefer longer labels as canonical (e.g. 'Review all details' over 'Review details')
  if (!acc[backend] || label.length > acc[backend].length) {
    acc[backend] = label;
  }
  return acc;
}, {} as Record<string, string>);

// Possible step statuses from lead_progress
export type StepStatus = 'COMPLETED' | 'COMPLETED_LOCKED' | 'IN_PROGRESS' | 'ERROR' | 'INVALIDATED' | 'LOCKED' | null;

// Map backend status values to our normalized StepStatus
export const normalizeStepStatus = (backendStatus: string | undefined): StepStatus => {
  if (!backendStatus) return null;
  const upper = backendStatus.toUpperCase();
  if (upper === 'SUCCESS' || upper === 'COMPLETED' || upper === 'DONE') return 'COMPLETED';
  if (upper === 'IN_PROGRESS' || upper === 'PENDING') return 'IN_PROGRESS';
  if (upper === 'ERROR' || upper === 'FAILED' || upper === 'FAILURE') return 'ERROR';
  if (upper === 'INVALIDATED') return 'INVALIDATED';
  if (upper === 'LOCKED') return 'LOCKED';
  return 'IN_PROGRESS'; // default for any unknown status that exists
};

export const transactionsOptionsList: Options[] = [
  {
    label: 'Payments',
    value: UiRoutes.PAYMENT,
    iconName: Icons.paymentsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_PAYMENT,
  },
  {
    label: 'Refunds',
    value: UiRoutes.REFUNDS,
    iconName: Icons.refundsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_REFUNDS,
  },
  {
    label: 'Settlements',
    value: UiRoutes.SETTLEMENTS,
    iconName: Icons.settlementsSidebar,
    clevertapEventName:
      CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_SETTLEMENTS,
  },
];

export const edgeOptionList: Options[] = [
  {
    label: 'Payments',
    value: UiRoutes.EDGE_TRANSACTIONS,
    iconName: Icons.paymentsSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_PAYMENT,
  },
  {
    label: 'Refunds',
    value: UiRoutes.EDGE_REFUNDS,
    iconName: Icons.refundsSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_REFUNDS,
  },
  {
    label: 'Settlements',
    value: UiRoutes.UPDATED_EDGE_SETTLEMENTS,
    iconName: Icons.settlementsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_SETTLEMENTS,
  },
  {
    label: 'Chargebacks',
    value: UiRoutes.EDGE_DISPUTES,
    iconName: Icons.disputesSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_DISPUTES,
  },
  {
    label: 'Reports',
    value: UiRoutes.MERCHANT_REPORT,
    iconName: Icons.reportsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_REPORTS,
  },
];

export const partnerOptionList: Options[] = [
  {
    label: 'Summary',
    value: UiRoutes.PARTNER_SUMMARY,
    iconName: Icons.partnerSummary,
  },
  {
    label: 'All Merchants',
    value: UiRoutes.PARTNER_ALL_MERCHANTS,
    iconName: Icons.partnerAllMerchant,
  },
];

export const partnerFeaturesList: Options[] = [
  {
    label: 'Transactions',
    value: UiRoutes.PARTNER_TRANSACTIONS,
    iconName: Icons.partnerTransactionSidebar,
  },
  {
    label: 'Commissions',
    value: UiRoutes.PARTNER_COMMISSIONS,
    iconName: Icons.reportsSidebar,
  },
  {
    label: 'Reports',
    value: UiRoutes.PARTNER_REPORTS,
    iconName: Icons.reportsSidebar,
  },
];

export const edgeV3OptionList: Options[] = [
  {
    label: 'Payments',
    value: UiRoutes.V3_PAYMENTS,
    iconName: Icons.paymentsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_PAYMENT,
  },
  {
    label: 'Refunds',
    value: UiRoutes.V3_REFUNDS,
    iconName: Icons.refundsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_REFUNDS,
  },
  {
    label: 'Settlements',
    value: UiRoutes.UPDATED_EDGE_SETTLEMENTS,
    iconName: Icons.settlementsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_SETTLEMENTS,
  },
  {
    label: 'Chargebacks',
    value: UiRoutes.EDGE_DISPUTES,
    iconName: Icons.disputesSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_DISPUTES,
  },
  {
    label: 'Reports',
    value: UiRoutes.MERCHANT_REPORT,
    iconName: Icons.reportsSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_REPORTS,
  },
];

export const payoutsOptionsList: Options[] = [
  // {
  //   label: 'Home',
  //   value: UiRoutes.PAYOUTS,
  //   icon: imgURL['home-icon'],
  // },
  {
    label: 'Individual Payouts',
    value: UiRoutes.INDIVIDUAL_PAYOUTS,
    iconName: Icons.individualPayoutsSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_INDIVIDUAL_PAYOUTS,
  },
  {
    label: 'Bulk Payouts',
    value: UiRoutes.BULK_PAYOUTS,
    iconName: Icons.bulkPayoutsSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_BULK_PAYOUTS,
  },
  {
    label: 'Settings',
    value: UiRoutes.PAYOUTS_SETTINGS,
    iconName: Icons.settingsSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_PAYOUTS_SETTING,
  },
  // {
  //   label: 'Bulk Beneficiaries',
  //   value: UiRoutes.BULK_BENEFICIARIES,
  //   icon: imgURL['beneficiaries-icon'],
  // },
  // {
  //   label: 'Payout Links',
  //   value: UiRoutes.PAYOUT_LINKS,
  //   icon: imgURL['payout-links-icon'],
  // },
  // {
  //   label: 'Beneficiaries',
  //   value: UiRoutes.BENEFICIARIES,
  //   icon: imgURL['beneficiaries-icon'],
  // },
  // {
  //   label: 'API Documentation',
  //   value: UiRoutes.API_DOCUMENTATION,
  //   icon: imgURL['api-documentation-icon'],
  // },
];

export const subscriptionOptionsList: Options[] = [
  {
    label: 'Plans',
    value: UiRoutes.V3_PLANS,
    icon: imgURL['plans-icon'],
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_PLANS,
  },
  {
    label: 'Subscriptions',
    value: UiRoutes.V3_SUBSCRIPTIONS,
    icon: imgURL['subscription-icon'],
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_SUBSCRIPTIONS,
  },
  {
    label: 'Presentations',
    value: UiRoutes.V3_PRESENTATIONS,
    icon: imgURL['subscription-icon'],
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_SUBSCRIPTIONS,
  },
];

export const valueAddedFeaturesList: (
  merchantTPVEnable: boolean,
  merchantSubscriptionEnabled: boolean
) => Options[] = (merchantTPVEnabled, merchantSubscriptionEnabled) => [
  {
    label: 'Smart Routing',
    value: UiRoutes.SMART_ROUTING,
    iconName: Icons.smartRouting,
    clevertapEventName:
      CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_SMART_ROUTING,
  },
  {
    label: 'Offers',
    value: UiRoutes.OFFERS,
    iconName: Icons.offersSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_OFFERS,
    isHidden: !checkPermissions('VIEW_OFFERS'),
  },
  {
    label: 'Plans',
    value: UiRoutes.PLANS,
    iconName: Icons.plansSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_PLANS,
    isHidden: !merchantSubscriptionEnabled,
  },
  {
    label: 'Subscriptions',
    value: UiRoutes.SUBSCRIPTIONS,
    iconName: Icons.subcriptionsSidebar,
    clevertapEventName:
      CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_SUBSCRIPTIONS,
    isHidden: !merchantSubscriptionEnabled,
  },
  {
    label: 'Presentations',
    value: UiRoutes.PRESENTATIONS,
    iconName: Icons.subcriptionsSidebar,
    clevertapEventName:
      CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_SUBSCRIPTIONS,
    isHidden: !merchantSubscriptionEnabled,
  },
  {
    label: 'Plural Links',
    value: UiRoutes.PAYMENT_LINKS,
    iconName: Icons.paymentLinksSidebar,
    clevertapEventName:
      CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_PAYMENT_LINKS,
    isHidden: merchantTPVEnabled,
  },
  // {
  //   label: 'Payment Pages',
  //   value: UiRoutes.PAYMENT_PAGES,
  //   icon: imgURL['payment-page-icon'],
  //   clevertapEventName:
  //     CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_PAYMENT_PAGES,
  // },
  {
    label: 'Analytics',
    value: UiRoutes.ANALYTICS,
    iconName: Icons.analyticSidebar,
    clevertapEventName: CleverTapEventConfigName.SIDEBAR_TAB_CLICKED_ANALYTICS,
  },
];

export const rcuOptionSearchList: Options[] = [
  {
    label: 'Transactions',
    value: UiRoutes.ADMIN_TRANSACTIONS,
    iconName: Icons.paymentsSidebar,
  },
  {
    label: 'Merchant',
    value: UiRoutes.ADMIN_RCU_MERCHANT,
    icon: imgURL['merchant-sidebar-logo'],
  },
];

export const edgeValueAddedFeaturesList: (
  edgeMerchantPaymentLinkEnabled: boolean,
  isBrandWalletEnabled?: boolean,
  isSelfGeneratedAccount?: boolean
) => Options[] = (edgeMerchantPaymentLinkEnabled, isBrandWalletEnabled = false, isSelfGeneratedAccount = false) => [
  {
    label: 'Payment Links',
    value: UiRoutes.EDGE_PAYMENT_LINKS,
    iconName: Icons.paymentLinksSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_PAYMENT_LINKS,
    isHidden: !edgeMerchantPaymentLinkEnabled || isSelfGeneratedAccount,
  },
  {
    label: 'Brand Wallet',
    value : UiRoutes.BRAND_WALLET,
    iconName: Icons.brandWalletSidebar,
    clevertapEventName: CleverTapEventNames.SIDEBAR_TAB_CLICKED_BRAND_WALLET,
    isHidden: !isBrandWalletEnabled,
  }
];

export const adminOptionList: Options[] = [
  {
    label: 'Merchants',
    value: UiRoutes.UNIFIED_MERCHANT_LISTING,
    iconName: Icons.merchant,

  },
  {
    label: 'Custom Routing',
    value: UiRoutes.ADMIN_CUSTOM_ROUTING,
    iconName: Icons.smartRouting,
  },
  {
    label: 'Analytics',
    value: UiRoutes.ADMIN_ANALYTICS,
    iconName: Icons.analyticSidebar,
  },
  {
    label: 'Users',
    value: UiRoutes.ADMIN_USER_MANAGEMENT,
    iconName: Icons.adminUser,
  },
];

const pgRouterAdminToolIcons: Record<string, Icons> = {
  Dashboard: Icons.overviewSidebar,
  Approvals: Icons.smartRouting,
  'Routing Rules': Icons.smartRouting,
  'Create Routing Rule': Icons.smartRouting,
  Simulation: Icons.smartRouting,
  'Add Merchant': Icons.merchant,
  Observability: Icons.analyticSidebar,
  'Sub-System Mapping': Icons.merchant,
  'View Rules': Icons.smartRouting,
  'Create New Rule': Icons.smartRouting,
};

export const pgRouterAdminToolsList: Options[] = PG_ROUTER_ADMIN_LINKS.map((link) => ({
  ...link,
  iconName: pgRouterAdminToolIcons[link.label] ?? Icons.smartRouting,
}));

export const adminDashboardOptionList: Options[] = [
  {
    label: 'Force Refund',
    value: UiRoutes.FORCE_REFUND_ADMIN,
    iconName: Icons.refundsSidebar,
  },
  {
    label: 'Force Update Order',
    value: UiRoutes.FORCE_UPDATE_ORDER_ADMIN,
    iconName: Icons.refundsSidebar,
  },
  {
    label: 'Fee Configuration',
    value: UiRoutes.FEE_CONFIGURATION_ADMIN,
    iconName: Icons.refundsSidebar,
  },
];

export const merchantVersionUpdateTab: Options = {
  label: 'Checkout Version',
  value: UiRoutes.UNIFIED_MERCHANT_VERSION_LISTING,
  iconName: Icons.smartRouting,
};

export const knowledgeBaseTab: Options = {
  label: 'Knowledge Base',
  value: UiRoutes.KNOWLEDGE_BASE_ADMIN,
  iconName: Icons.settingsSidebar,
};

export const adminOptionListWithOps = [
  ...adminOptionList,
  {
    label: 'Bulk Refunds',
    value: UiRoutes.OPS_REFUNDS_BULK_UPLOAD_HISTORY,
    iconName: Icons.refundsSidebar,
  },
  {
    label: 'Bulk IMEI',
    value: UiRoutes.OPS_IMEI_BULK_UPLOAD_HISTORY,
    iconName: Icons.paymentsSidebar,

  },
];

export const opsAdminOptionList: Options[] = [
  {
    label: 'Users',
    value: UiRoutes.ADMIN_USER_MANAGEMENT,
    iconName: Icons.adminUser,
  },
  {
    label: 'Bulk Refunds',
    value: UiRoutes.OPS_REFUNDS_BULK_UPLOAD_HISTORY,
    iconName: Icons.refundsSidebar,
  },
  {
    label: 'Bulk IMEI',
    value: UiRoutes.OPS_IMEI_BULK_UPLOAD_HISTORY,
    iconName: Icons.paymentsSidebar,
  },
];

export const headerDropdownDetails = {
  Router: {
    icon: imgURL['unified-router-icon'],
    text: 'Payment Console',
  },
  Edge: {
    icon: isCiti ? imgURL['citi-unified-gateway-icon'] : imgURL['unified-gateway-icon'],
    text: 'Payment Gateway',
  },
  Payout: {
    icon: imgURL['unified-payout-icon'],
    text: 'Payouts',
  },
  Subscriptions: {
    icon: imgURL['unified-subscription-icon'],
    text: 'Subscriptions',
  },
};


export const DefaultStepsData = [
  'PAN & GST',
  'Bank account',
  'KYC verification',
  'Video KYC',
  'Review all details'
];

export const BPCLStepsData = [
  'PAN & GST',
  'Bank account',
  'KYC verification',
  'Video KYC',
  'Review all details',
];

export const StepperStepCount = {
  '/user-verification/product-selection': 1,
  '/user-verification/pan-verification': 2,
  '/user-verification/business-verification': 3,
  '/user-verification/bank-account-verification': 4,
  '/user-verification/kyc-verification': 5,
  '/user-verification/video-kyc-verification': 6,
  '/user-verification/review-details': 7
};

export const DefaultStepperStepCount = [
  '/user-verification/pan-verification',
  '/user-verification/bank-account-verification',
  '/user-verification/kyc-verification',
  '/user-verification/video-kyc-verification',
  '/user-verification/review-details'
];
export const BPCLSolePropStepper = [
  '/user-verification/pan-verification',
  '/user-verification/bank-account-verification',
  '/user-verification/kyc-verification',
  '/user-verification/video-kyc-verification',
  '/user-verification/review-details',
];

export const CinLlpStepperStepCount = [
  '/user-verification/pan-verification',
  '/user-verification/bank-account-verification',
  '/user-verification/upload-documents',
  '/user-verification/review-details',
  '/user-verification/kyc-sign-agreement',
];


export const FLOW_CIN_LLP_STEP_DATA = [
  'PAN & GST',
  'Bank account',
  'Upload documents',
  'Review details',
  'KYC & Sign Agreement',
];

const BASE_DEFAULT_STEPS_DATA = [...DefaultStepsData];
const BASE_BPCL_STEPS_DATA = [...BPCLStepsData];
const BASE_CIN_STEPS_DATA = [...FLOW_CIN_LLP_STEP_DATA];
const BASE_DEFAULT_STEPPER = [...DefaultStepperStepCount];
const BASE_BPCL_STEPPER = [...BPCLSolePropStepper];
const BASE_CIN_STEPPER = [...CinLlpStepperStepCount];

export const resetStepperFlows = () => {
  DefaultStepsData.splice(0, DefaultStepsData.length, ...BASE_DEFAULT_STEPS_DATA);
  BPCLStepsData.splice(0, BPCLStepsData.length, ...BASE_BPCL_STEPS_DATA);
  FLOW_CIN_LLP_STEP_DATA.splice(0, FLOW_CIN_LLP_STEP_DATA.length, ...BASE_CIN_STEPS_DATA);
  DefaultStepperStepCount.splice(0, DefaultStepperStepCount.length, ...BASE_DEFAULT_STEPPER);
  BPCLSolePropStepper.splice(0, BPCLSolePropStepper.length, ...BASE_BPCL_STEPPER);
  CinLlpStepperStepCount.splice(0, CinLlpStepperStepCount.length, ...BASE_CIN_STEPPER);
};

const insertStepAfter = (
  stepData: string[],
  stepperRoutes: string[],
  anchorLabel: string,
  stepLabel: string,
  stepRoute: string,
) => {
  let insertIndex = stepData.indexOf(stepLabel);

  if (insertIndex === -1) {
    const anchorIndex = stepData.indexOf(anchorLabel);
    insertIndex = anchorIndex !== -1
      ? anchorIndex + 1
      : Math.min(1, stepData.length);
    stepData.splice(insertIndex, 0, stepLabel);
  }

  if (!stepperRoutes.includes(stepRoute)) {
    stepperRoutes.splice(insertIndex, 0, stepRoute);
  }
};

export const addBusinessVerificationToFlow = () => {
  insertStepAfter(
    DefaultStepsData,
    DefaultStepperStepCount,
    'PAN & GST',
    'Website details',
    UiRoutes.BUSINESS_DETAILS,
  );
  insertStepAfter(
    BPCLStepsData,
    BPCLSolePropStepper,
    'PAN & GST',
    'Website details',
    UiRoutes.BUSINESS_DETAILS,
  );
  insertStepAfter(
    FLOW_CIN_LLP_STEP_DATA,
    CinLlpStepperStepCount,
    'PAN & GST',
    'Website details',
    UiRoutes.BUSINESS_DETAILS,
  );
};

export const getBusinessDetailsMode = (onboardingConfigurations?: {
  business_details_mode?: string;
  businessDetailsMode?: string;
  business_verification_mode?: string;
  businessVerificationMode?: string;
  [key: string]: any;
}): string | undefined =>
  onboardingConfigurations?.business_details_mode ||
  onboardingConfigurations?.businessDetailsMode ||
  onboardingConfigurations?.business_verification_mode ||
  onboardingConfigurations?.businessVerificationMode;

export const shouldShowBusinessDetailsStep = (stepperData?: {
  onboarding_configurations?: {
    business_details_mode?: string;
    businessDetailsMode?: string;
    business_verification_mode?: string;
    businessVerificationMode?: string;
    [key: string]: any;
  };
  [key: string]: any;
}): boolean => {
  const data = stepperData || {};
  const onboardingConfigurations = data?.onboarding_configurations;

  return getBusinessDetailsMode(onboardingConfigurations) === 'CHOOSE' ||
    (Object.keys(data).length > 0 && !onboardingConfigurations);
};

/**
 * Mutates DefaultStepsData / DefaultStepperStepCount AND
 * BPCLSolePropStepper / BPCLStepsData to insert the
 * 'Business Verification' step (sole-prop document upload) between
 * 'Bank Account' and 'KYC verification'.
 * Called for INDIVIDUAL pan-type merchants in the sidebar and UserVerificationBox.
 */
export const addSolePropDocumentsUploadToFlow = () => {
  // ── Default flow (used by most self-managed INDIVIDUAL merchants) ────
  const defaultBankIndex = DefaultStepsData.indexOf('Bank account');
  if (defaultBankIndex !== -1) {
    if (!DefaultStepsData.includes('Business verification')) {
      DefaultStepsData.splice(defaultBankIndex + 1, 0, 'Business verification');
    }
    if (!DefaultStepperStepCount.includes(UiRoutes.SOLE_PROP_DOCUMENTS_UPLOAD)) {
      DefaultStepperStepCount.splice(defaultBankIndex + 1, 0, UiRoutes.SOLE_PROP_DOCUMENTS_UPLOAD);
    }
  }

  // ── BPCL / parent-managed flow ───────────────────────────────────────
  const bpclBankIndex = BPCLStepsData.indexOf('Bank account');
  if (bpclBankIndex !== -1) {
    if (!BPCLStepsData.includes('Business verification')) {
      BPCLStepsData.splice(bpclBankIndex + 1, 0, 'Business verification');
    }
    if (!BPCLSolePropStepper.includes(UiRoutes.SOLE_PROP_DOCUMENTS_UPLOAD)) {
      BPCLSolePropStepper.splice(bpclBankIndex + 1, 0, UiRoutes.SOLE_PROP_DOCUMENTS_UPLOAD);
    }
  }
};

export const addUploadCompanyDocumentsToFlow = () => {
  // Delete  KYC verification
  const kycVerificationIndex = DefaultStepsData.indexOf('KYC verification');
  if (kycVerificationIndex !== -1) {
    DefaultStepsData.splice(kycVerificationIndex, 1);
    DefaultStepperStepCount.splice(kycVerificationIndex, 1);
  }

  // 1. Delete video kyc step (Safely)
  const videoKycIndex = DefaultStepsData.indexOf('Video KYC');
  
  // FIX: Only splice if the item actually exists (index is not -1)
  if (videoKycIndex !== -1) {
    DefaultStepsData.splice(videoKycIndex, 1);
    DefaultStepperStepCount.splice(videoKycIndex, 1);
  }

  // 2. Insert Business Verification
  const bankAccountIndex = DefaultStepsData.indexOf('Bank account');
  
  // FIX: Only insert if 'Bank Account' exists to avoid inserting at index 0
  if (bankAccountIndex !== -1) {
    
    // Insert Step Name
    if (!DefaultStepsData.includes('Business verification')) {
      DefaultStepsData.splice(bankAccountIndex + 1, 0, 'Business verification');
    }
    
    // Insert Route Path
    // We check if the route at the target index is already correct to prevent duplicates
    if (DefaultStepperStepCount[bankAccountIndex + 1] !== '/user-verification/upload-documents-company') {
      DefaultStepperStepCount.splice(bankAccountIndex + 1, 0, '/user-verification/upload-documents-company');
    }
  }

  // 3. Insert KYC & Sign Agreement
  const kycSignAgreementIndex = DefaultStepsData.indexOf('KYC & Sign Agreement');
  
  if (kycSignAgreementIndex == -1) {
    
    // Insert Step Name
    if (!DefaultStepsData.includes('KYC & Sign Agreement')) {
      DefaultStepsData.push('KYC & Sign Agreement');
    }
    
    // Insert Route Path
    if (!DefaultStepperStepCount.includes('/user-verification/kyc-sign-agreement')) {
      DefaultStepperStepCount.push('/user-verification/kyc-sign-agreement');
    }
  }
};
  

export interface Options {
  label: string;
  value: string;
  icon?: string;
  clevertapEventName?: CleverTapEventConfigName | string;
  isHidden?: boolean;
  showMenu?: boolean;
  iconName?: Icons;
}
